import React, { useEffect, useState, useCallback } from 'react';
import {
  Bot, Clock, Zap, Hash, Activity, Save, Loader2, Edit2,
  FileText, Cpu, Wrench, Search, X, Plus, ChevronRight, Info,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { FOREST_GREEN } from '@/styles/tokens';
import {
  frameworkApi,
  FrameworkAgentConfig, AgentToolEntry,
} from '@/services/api/agentApi';

interface FrameworkAgentDetailProps {
  agent: {
    _id?: string;
    id?: string;
    instanceId?: string;
    typeName?: string;
    name: string;
    status?: string;
    role?: string;
    description?: string;
    capabilities?: string[];
    tier?: number;
    createdAt?: string;
    lastActiveAt?: string;
    nextScheduledWake?: string;
  };
}

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function InfoTip({ text }: { text: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs max-w-[220px]">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const TIER_LABELS: Record<number, { label: string; description: string }> = {
  1: { label: 'Frontier', description: 'Complex reasoning tasks' },
  2: { label: 'Capable', description: 'Standard tasks' },
  3: { label: 'Fast', description: 'Quick, simple tasks' },
};

// ─── Overview Tab ────────────────────────────────────────────────────────────

function OverviewTab({ agent, instanceId }: { agent: FrameworkAgentDetailProps['agent']; instanceId: string }) {
  const [displayName, setDisplayName] = useState(agent.name || '');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await frameworkApi.updateAgent(instanceId, { display_name: displayName });
      setDirty(false);
    } catch { /* silent */ }
    setSaving(false);
  };

  const tierInfo = agent.tier != null ? TIER_LABELS[agent.tier] : null;

  return (
    <div className="space-y-4">
      {/* Editable name */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Display Name</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={displayName}
            onChange={(e) => { setDisplayName(e.target.value); setDirty(true); }}
            className="flex-1 px-3 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          {dirty && (
            <Button onClick={handleSave} disabled={saving} size="sm" className="gap-1.5" style={{ backgroundColor: FOREST_GREEN.DEFAULT }}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </Button>
          )}
        </div>
      </div>

      {/* Description */}
      {agent.description && (
        <div className="bg-muted rounded-lg p-3">
          <p className="text-sm text-foreground/80 leading-relaxed">{agent.description}</p>
        </div>
      )}

      {/* Details grid */}
      <div className="grid grid-cols-2 gap-3">
        {tierInfo && (
          <div className="border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Model Tier</span>
            </div>
            <p className="text-sm font-medium text-foreground">Tier {agent.tier} — {tierInfo.label}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{tierInfo.description}</p>
          </div>
        )}

        {agent.lastActiveAt && (
          <div className="border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Last Active</span>
            </div>
            <p className="text-sm text-foreground">{timeAgo(agent.lastActiveAt)}</p>
          </div>
        )}

        {agent.nextScheduledWake && (
          <div className="border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Next Wake</span>
            </div>
            <p className="text-sm text-foreground">{new Date(agent.nextScheduledWake).toLocaleString()}</p>
          </div>
        )}
      </div>

      {/* Capabilities */}
      {agent.capabilities && agent.capabilities.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Capabilities</h4>
          <div className="flex flex-wrap gap-1.5">
            {agent.capabilities.map(cap => (
              <span key={cap} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-md">
                {cap}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Type info */}
      {agent.typeName && (
        <div className="border-t border-border pt-3">
          <p className="text-xs text-muted-foreground">
            Type: <span className="font-mono">{agent.typeName}</span>
            {agent.createdAt && <> &middot; Created {timeAgo(agent.createdAt)}</>}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Instructions Tab ────────────────────────────────────────────────────────

function InstructionsTab({ instanceId }: { instanceId: string }) {
  const [config, setConfig] = useState<FrameworkAgentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    frameworkApi.getAgentConfig(instanceId)
      .then(r => {
        const c = r.data;
        setConfig(c);
        setSystemPrompt(c?.system_prompt || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [instanceId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await frameworkApi.updateAgentConfig(instanceId, { system_prompt: systemPrompt });
      setConfig(res.data);
      setDirty(false);
    } catch { /* silent */ }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Custom instructions shape how this agent behaves, responds, and reasons. These are prepended to every conversation.
      </p>

      <div className="space-y-2">
        <label className="text-sm font-medium">System Prompt</label>
        <textarea
          value={systemPrompt}
          onChange={(e) => { setSystemPrompt(e.target.value); setDirty(true); }}
          placeholder="e.g., Focus on actionable suggestions. When discussing data, include relevant metrics..."
          rows={10}
          className="w-full px-3 py-2 text-sm font-mono border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
        />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">{systemPrompt.length} characters</span>
          {dirty && (
            <Button onClick={handleSave} disabled={saving} size="sm" className="gap-1.5" style={{ backgroundColor: FOREST_GREEN.DEFAULT }}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save Instructions
            </Button>
          )}
        </div>
      </div>

      {/* Knowledge Entries */}
      {config && <KnowledgeEntriesSection instanceId={instanceId} config={config} onUpdate={setConfig} />}
    </div>
  );
}

// ─── Knowledge Entries Section ───────────────────────────────────────────────

function KnowledgeEntriesSection({
  instanceId,
  config,
  onUpdate,
}: {
  instanceId: string;
  config: FrameworkAgentConfig;
  onUpdate: (c: FrameworkAgentConfig) => void;
}) {
  const [entries, setEntries] = useState<string[]>(config.knowledge_entries || []);
  const [newEntry, setNewEntry] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const handleAdd = () => {
    if (!newEntry.trim()) return;
    setEntries([...entries, newEntry.trim()]);
    setNewEntry('');
    setDirty(true);
  };

  const handleRemove = (idx: number) => {
    setEntries(entries.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await frameworkApi.updateAgentConfig(instanceId, { knowledge_entries: entries });
      onUpdate(res.data);
      setDirty(false);
    } catch { /* silent */ }
    setSaving(false);
  };

  return (
    <div className="space-y-3 pt-4 border-t border-border">
      <div className="flex items-center gap-1.5">
        <h4 className="text-sm font-medium">Permanent Knowledge</h4>
        <InfoTip text="Facts this agent should always know. These never decay, unlike auto-generated memories." />
      </div>

      {entries.length > 0 && (
        <div className="space-y-1.5">
          {entries.map((entry, idx) => (
            <div key={idx} className="flex items-start gap-2 p-2 bg-muted rounded-lg group">
              <span className="text-sm flex-1">{entry}</span>
              <button
                onClick={() => handleRemove(idx)}
                className="p-1 opacity-0 group-hover:opacity-100 hover:bg-muted rounded text-muted-foreground hover:text-foreground/80 transition-opacity"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={newEntry}
          onChange={(e) => setNewEntry(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          placeholder="Add a permanent knowledge entry..."
          className="flex-1 px-3 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <Button size="sm" variant="outline" onClick={handleAdd} disabled={!newEntry.trim()}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>

      {dirty && (
        <Button onClick={handleSave} disabled={saving} size="sm" className="gap-1.5" style={{ backgroundColor: FOREST_GREEN.DEFAULT }}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save Knowledge
        </Button>
      )}
    </div>
  );
}

// ─── Model & Resources Tab ──────────────────────────────────────────────────

const MODEL_TIERS = [
  { tier: 1, label: 'Frontier', description: 'Complex reasoning, slower, higher cost' },
  { tier: 2, label: 'Capable', description: 'Balanced quality and speed' },
  { tier: 3, label: 'Fast', description: 'Quick responses, lower cost' },
];

const BUDGET_OPTIONS = [
  { value: 'low', label: 'Low', description: 'Minimal resource usage' },
  { value: 'medium', label: 'Medium', description: 'Balanced allocation' },
  { value: 'high', label: 'High', description: 'Priority processing' },
  { value: 'critical', label: 'Critical', description: 'Maximum resources' },
] as const;

type BudgetPriority = (typeof BUDGET_OPTIONS)[number]['value'];

function ModelResourcesTab({ instanceId }: { instanceId: string }) {
  const [config, setConfig] = useState<FrameworkAgentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modelTier, setModelTier] = useState(2);
  const [budget, setBudget] = useState<BudgetPriority>('medium');
  const [maxExperts, setMaxExperts] = useState(2);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    frameworkApi.getAgentConfig(instanceId)
      .then(r => {
        const c = r.data;
        setConfig(c);
        if (c) {
          setModelTier(c.resource_preferences?.default_model_tier || 2);
          setBudget(c.resource_preferences?.budget_priority || 'medium');
          setMaxExperts(c.resource_preferences?.max_expert_agents || 2);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [instanceId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await frameworkApi.updateAgentConfig(instanceId, {
        resource_preferences: {
          default_model_tier: modelTier,
          budget_priority: budget,
          max_expert_agents: maxExperts,
        },
      });
      setConfig(res.data);
      setDirty(false);
    } catch { /* silent */ }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground">
        Control the intelligence level, resource allocation, and sub-agent limits for this agent.
      </p>

      {/* Model Tier */}
      <div className="space-y-3">
        <div className="flex items-center gap-1.5">
          <h4 className="text-sm font-medium">Model Tier</h4>
          <InfoTip text="Higher tiers use more capable (and expensive) AI models. The agent auto-falls back if the selected tier is unavailable." />
        </div>
        <div className="space-y-2">
          {MODEL_TIERS.map(t => (
            <label
              key={t.tier}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                modelTier === t.tier
                  ? 'border-primary/50 bg-primary/10'
                  : 'border-border hover:border-border/80'
              }`}
            >
              <input
                type="radio"
                name="modelTier"
                value={t.tier}
                checked={modelTier === t.tier}
                onChange={() => { setModelTier(t.tier); setDirty(true); }}
                className="mt-0.5 accent-emerald-600"
              />
              <div className="flex-1">
                <span className="text-sm font-medium">Tier {t.tier} — {t.label}</span>
                <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Budget Priority */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <h4 className="text-sm font-medium">Budget Priority</h4>
          <InfoTip text="Determines how much of the organization's AI budget this agent can consume." />
        </div>
        <div className="grid grid-cols-4 gap-2">
          {BUDGET_OPTIONS.map(o => (
            <button
              key={o.value}
              onClick={() => { setBudget(o.value); setDirty(true); }}
              className={`p-2 rounded-lg border text-center transition-colors ${
                budget === o.value
                  ? 'border-primary/50 bg-primary/10'
                  : 'border-border hover:border-border/80'
              }`}
            >
              <p className="text-xs font-medium">{o.label}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{o.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Max Expert Agents */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <h4 className="text-sm font-medium">Max Expert Agents</h4>
          <InfoTip text="Maximum sub-agents this agent can spawn for complex tasks. More agents = faster but costlier." />
        </div>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="0"
            max="10"
            value={maxExperts}
            onChange={(e) => { setMaxExperts(parseInt(e.target.value)); setDirty(true); }}
            className="flex-1"
          />
          <span className="text-sm font-mono w-8 text-right">{maxExperts}</span>
        </div>
        <p className="text-[10px] text-muted-foreground">
          {maxExperts === 0 ? 'No sub-agents — handles everything alone' : `Up to ${maxExperts} sub-agent${maxExperts > 1 ? 's' : ''} per task`}
        </p>
      </div>

      {dirty && (
        <Button onClick={handleSave} disabled={saving} size="sm" className="gap-1.5" style={{ backgroundColor: FOREST_GREEN.DEFAULT }}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save Resources
        </Button>
      )}
    </div>
  );
}

// ─── Tools Catalog Tab ──────────────────────────────────────────────────────

const TIER_BADGE: Record<string, { label: string; className: string }> = {
  READ_ONLY: { label: 'Read Only', className: 'bg-primary/10 text-primary' },
  REVERSIBLE: { label: 'Reversible', className: 'bg-amber-50 text-amber-700' },
  IRREVERSIBLE: { label: 'Irreversible', className: 'bg-red-50 text-red-700' },
  DERIVED: { label: 'Derived', className: 'bg-muted text-muted-foreground' },
};

function ToolsCatalogTab({ instanceId }: { instanceId: string }) {
  const [tools, setTools] = useState<AgentToolEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [expandedTool, setExpandedTool] = useState<string | null>(null);

  useEffect(() => {
    frameworkApi.getAgentTools(instanceId)
      .then(r => setTools(r.data?.tools || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [instanceId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (tools.length === 0) {
    return (
      <div className="text-center py-8">
        <Wrench className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
        <p className="text-sm text-muted-foreground">No tools available yet.</p>
      </div>
    );
  }

  const q = searchInput.toLowerCase();
  const filtered = q
    ? tools.filter(t =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
      )
    : tools;

  const categories = new Map<string, AgentToolEntry[]>();
  for (const tool of filtered) {
    const cat = tool.category || 'Other';
    const categoryTools = categories.get(cat);
    if (categoryTools) {
      categoryTools.push(tool);
    } else {
      categories.set(cat, [tool]);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Tools this agent can use. Safety tiers determine whether confirmation is needed before execution.
      </p>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search tools..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          className="w-full pl-9 pr-8 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        {searchInput && (
          <button
            onClick={() => setSearchInput('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground/80"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">{filtered.length} tool{filtered.length !== 1 ? 's' : ''} available</p>

      {Array.from(categories.entries()).map(([category, categoryTools]) => (
        <div key={category}>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">{category}</h4>
          <div className="space-y-1.5">
            {categoryTools.map(tool => {
              const tierBadge = TIER_BADGE[tool.tier] || TIER_BADGE.DERIVED;
              const isExpanded = expandedTool === tool.name;
              return (
                <div
                  key={tool.name}
                  className="border border-border rounded-lg p-3 cursor-pointer hover:border-border/80 transition-colors"
                  onClick={() => setExpandedTool(isExpanded ? null : tool.name)}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{tool.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${tierBadge.className}`}>{tierBadge.label}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${tool.access === 'required' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                          {tool.access === 'required' ? 'Required' : 'Optional'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{tool.description}</p>
                    </div>
                    <ChevronRight className={`h-4 w-4 text-muted-foreground/50 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </div>

                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-border space-y-2">
                      {tool.capability_tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {tool.capability_tags.map(tag => (
                            <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded">{tag}</span>
                          ))}
                        </div>
                      )}
                      {tool.side_effects?.length > 0 && (
                        <p className="text-[10px] text-amber-600">Side effects: {tool.side_effects.join(', ')}</p>
                      )}
                      {tool.input_params?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-medium text-muted-foreground mb-1">Parameters</p>
                          <div className="space-y-0.5">
                            {tool.input_params.map(p => (
                              <p key={p.name} className="text-[10px] text-muted-foreground">
                                <span className="font-mono text-foreground/80">{p.name}</span>
                                <span className="text-muted-foreground"> ({p.type})</span>
                                {p.required && <span className="text-red-400 ml-1">*</span>}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function FrameworkAgentDetail({ agent }: FrameworkAgentDetailProps) {
  const instanceId = agent.instanceId || agent.id || agent._id || '';

  if (!instanceId) {
    return (
      <div className="px-4 py-8 text-center text-muted-foreground">
        <Bot className="h-10 w-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">Unable to load agent settings — no instance ID found.</p>
      </div>
    );
  }

  const contentHeight = 'h-[calc(75vh-100px)]';

  return (
    <Tabs defaultValue="overview" className="flex flex-col h-full">
      <div className="border-b border-border px-4">
        <TabsList className="w-full">
          <TabsTrigger value="overview" className="flex-1 text-xs">Overview</TabsTrigger>
          <TabsTrigger value="instructions" className="flex-1 text-xs">Instructions</TabsTrigger>
          <TabsTrigger value="model" className="flex-1 text-xs">Model & Resources</TabsTrigger>
          <TabsTrigger value="tools" className="flex-1 text-xs">Tools</TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="overview" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
        <OverviewTab agent={agent} instanceId={instanceId} />
      </TabsContent>

      <TabsContent value="instructions" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
        <InstructionsTab instanceId={instanceId} />
      </TabsContent>

      <TabsContent value="model" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
        <ModelResourcesTab instanceId={instanceId} />
      </TabsContent>

      <TabsContent value="tools" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
        <ToolsCatalogTab instanceId={instanceId} />
      </TabsContent>
    </Tabs>
  );
}
